const ENTRY_FAMILIES = [
  { key: "noise", path: "noise", label: "Noise" },
  { key: "decoy", path: "decoy", label: "Decoy" },
  { key: "departments", path: "department", label: "Departments" },
];

// A stable key for one entry: "<family path>:<name>".
export function entryKey(familyPath, name) {
  return `${familyPath}:${name}`;
}

// The run's entries grouped by family, empty groups dropped. An old scenario
// has none, so callers can treat an empty result as "no entries".
export function getEntryGroups(run) {
  return ENTRY_FAMILIES.map((family) => ({
    family,
    entries: run?.[family.key] || [],
  })).filter((group) => group.entries.length > 0);
}

// True when the run carries at least one entry across all families.
export function runHasEntries(run) {
  return ENTRY_FAMILIES.some((family) => (run?.[family.key] || []).length > 0);
}

// The first selectable entry, or null — used to seed the default selection.
export function firstEntryOf(run) {
  const groups = getEntryGroups(run);
  if (groups.length === 0) return null;
  const { family, entries } = groups[0];
  return { family: family.path, name: entries[0].name };
}

// A descriptive card for a noise/decoy entry. Selecting it (not firing) marks
// it; firing happens on the separate Inject button.
function EntryCard({ family, entry, isSelected, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(family.path, entry.name)}
      aria-pressed={isSelected}
      className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors ${
        isSelected
          ? "border-sky-500/60 bg-sky-500/5"
          : "border-(--color-border) bg-gray-50/80 dark:bg-black/20 hover:border-sky-500/50 hover:bg-sky-500/5"
      }`}
    >
      <span
        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
          isSelected ? "border-sky-500/60" : "border-sky-500/40"
        }`}
      >
        <span
          className={`h-1.5 w-1.5 rounded-full ${isSelected ? "bg-sky-400" : "bg-sky-400/50"}`}
        />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-xs font-semibold text-(--color-text-primary)">
          {entry.name}
        </span>
        {entry.description ? (
          // Collapsed to one line until selected, so the list stays scannable;
          // the chosen entry shows its full description.
          <span
            className={`mt-0.5 text-[11px] leading-relaxed text-(--color-text-muted) ${
              isSelected ? "block" : "line-clamp-1"
            }`}
          >
            {entry.description}
          </span>
        ) : null}
      </span>
    </button>
  );
}

// Departments are many and self-explanatory (HR, Finance, …), so they render
// as compact name-only chips instead of descriptive cards.
function EntryChip({ family, entry, isSelected, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(family.path, entry.name)}
      aria-pressed={isSelected}
      title={entry.description || entry.name}
      className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
        isSelected
          ? "border-sky-500/60 bg-sky-500/10 text-sky-400"
          : "border-(--color-border) bg-gray-50/80 dark:bg-black/20 text-(--color-text-secondary) hover:border-sky-500/50 hover:bg-sky-500/5 hover:text-sky-400"
      }`}
    >
      <span
        className={`h-1.5 w-1.5 shrink-0 rounded-full ${isSelected ? "bg-sky-400" : "bg-sky-400/50"}`}
      />
      {entry.name}
    </button>
  );
}

// Renders the run's entries grouped by family as a selectable list. Selection
// is lifted to the caller via `selectedKey` + `onSelect`; firing is a separate
// action (an Inject button the caller owns).
export default function EntryInjectGroups({ run, selectedKey, onSelect }) {
  const groups = getEntryGroups(run);

  if (groups.length === 0) {
    return (
      <p className="px-1 py-6 text-center text-xs text-(--color-text-muted)">
        This scenario has no noise, decoy, or department entries.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {groups.map(({ family, entries }) => (
        <div key={family.key} className="space-y-2">
          <p className="flex items-baseline gap-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-(--color-text-muted)">
            {family.label}
            <span className="font-mono text-[10px] tracking-normal text-(--color-text-muted)/70">
              {entries.length}
            </span>
          </p>
          {family.key === "departments" ? (
            <div className="flex flex-wrap gap-2">
              {entries.map((entry) => (
                <EntryChip
                  key={entry.name}
                  family={family}
                  entry={entry}
                  isSelected={selectedKey === entryKey(family.path, entry.name)}
                  onSelect={onSelect}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {entries.map((entry) => (
                <EntryCard
                  key={entry.name}
                  family={family}
                  entry={entry}
                  isSelected={selectedKey === entryKey(family.path, entry.name)}
                  onSelect={onSelect}
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
